import type { SessionMarket } from '@contract';

/**
 * The market this cell serves, for the words and figures the portal prints:
 * its own currency and time zone, and which providers it has. One cell per
 * tab, fixed for the life of the page (cells.ts), so this is set once, when
 * the signed-in layout has read `session.me`, and read from anywhere. Until
 * it is set, and when the server does not say (a cell that has not yet run the
 * session-market migration), it is Ghana's: the portal prints what it always
 * printed.
 */
export interface MarketInfo {
  code: string;
  name: string;
  homeCurrency: string;
  currencies: readonly string[];
  /** An IANA zone, such as Africa/Accra. */
  timezone: string;
  /** The cell's providers (any status) with their capabilities; null when the server did not say. */
  providers:
    | readonly {
        code: string;
        status?: string;
        capabilities: readonly string[];
      }[]
    | null;
}

export const GHANA_MARKET: MarketInfo = {
  code: 'GH',
  name: 'Ghana',
  homeCurrency: 'GHS',
  currencies: ['GHS', 'USD'],
  timezone: 'Africa/Accra',
  providers: null,
};

let active: MarketInfo = GHANA_MARKET;

export function marketFromSession(raw: SessionMarket | undefined): MarketInfo {
  if (!raw) return GHANA_MARKET;
  return {
    code: raw.code,
    name: raw.name,
    homeCurrency: raw.home_currency,
    currencies: raw.currencies,
    timezone: raw.timezone,
    providers: raw.providers,
  };
}

export function setActiveMarket(raw: SessionMarket | undefined): void {
  active = marketFromSession(raw);
}

export function activeMarket(): MarketInfo {
  return active;
}

/** The cell's home currency: where an amount carries no currency of its own. */
export function homeCurrency(): string {
  return active.homeCurrency;
}

/** A locale for grouping digits in the cell's country: en-GH for Ghana. */
export function numberLocale(): string {
  return `en-${active.code}`;
}

/** The city of the cell's time zone: "Accra" for Africa/Accra, "São Tomé" for Africa/Sao_Tome. */
export function zoneCity(zone: string = active.timezone): string {
  const city = zone.split('/').pop() ?? zone;
  return CITY_NAMES[city] ?? city.replaceAll('_', ' ');
}

/** Zone IDs are ASCII; these cities are written with their accents. */
const CITY_NAMES: Readonly<Record<string, string>> = { Sao_Tome: 'São Tomé' };

/** The zone's offset from UTC in minutes at `at` (0 for Accra, Monrovia, Freetown, Banjul and São Tomé). */
export function zoneOffsetMinutes(
  zone: string = active.timezone,
  at: Date = new Date(),
): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at);
    const get = (type: string) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
  } catch {
    return 0;
  }
}

/** "GMT" at zero, otherwise "GMT+1" or "GMT+5:30". */
export function offsetLabel(minutes: number = zoneOffsetMinutes()): string {
  if (minutes === 0) return 'GMT';
  const sign = minutes > 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `GMT${sign}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
}

/**
 * Whether the cell has a provider of this code, in any status (true when the
 * server did not say). A paused provider is still the cell's, so its pages
 * stay. Ghana always has its own providers: a paused or missing gh-hubtel
 * never hides them in Ghana's own portal.
 */
export function hasProvider(code: string): boolean {
  return (
    active.code === 'GH' ||
    active.providers === null ||
    active.providers.some((provider) => provider.code === code)
  );
}

/**
 * Whether any active provider of the cell has this capability (true when the
 * server did not say). Ghana always has its own: a paused Ghana provider
 * never hides its pages in Ghana's portal.
 */
export function hasCapability(capability: string): boolean {
  return (
    active.code === 'GH' ||
    active.providers === null ||
    active.providers.some(
      (provider) =>
        (provider.status === undefined || provider.status === 'active') &&
        provider.capabilities.includes(capability),
    )
  );
}

/** Hubtel's float, sweeps and Send to Bank belong to a cell that has Hubtel. */
export function hasHubtel(): boolean {
  return hasProvider('gh-hubtel');
}

/** Access Bank's GIP codes and the pool statement belong to a cell that has Access Bank. */
export function hasAccessBank(): boolean {
  return hasProvider('gh-access-bank');
}

/** The mobile money networks MTN, Telecel and AirtelTigo are Ghana's. */
export function hasGhanaMomoNetworks(): boolean {
  return active.code === 'GH';
}

/** The label of an hour picked in UTC: "Hour (Accra time, UTC)" where the cell's clock is UTC. */
export function hourLabel(): string {
  return zoneOffsetMinutes() === 0
    ? `Hour (${zoneCity()} time, UTC)`
    : 'Hour (UTC)';
}

const MINOR_NAME: Record<string, string> = {
  GHS: 'pesewas',
  USD: 'cents',
  LRD: 'cents',
  SLE: 'cents',
  GMD: 'bututs',
  STN: 'cêntimos',
};
const MAJOR_NAME: Record<string, string> = {
  GHS: 'cedis',
  USD: 'dollars',
  LRD: 'Liberian dollars',
  SLE: 'leones',
  GMD: 'dalasis',
  STN: 'dobras',
};

/** What the cell's minor unit is called: pesewas for GHS, cents for LRD. */
export function minorUnitName(): string {
  return MINOR_NAME[active.homeCurrency] ?? 'minor units';
}

/** What the cell's currency is called in a sentence: cedis for GHS, leones for SLE. */
export function majorUnitName(): string {
  return MAJOR_NAME[active.homeCurrency] ?? active.homeCurrency;
}

/**
 * Whether a setting belongs to a provider this cell has. A cell without
 * Hubtel hides the sweeps and Hubtel float checks; one without Access Bank
 * hides the GIP codes, the bank channel and the pool covers; one outside Ghana
 * hides the MTN, Telecel and AirtelTigo routes.
 */
export function settingBelongsHere(key: string): boolean {
  if (
    (key.startsWith('sweeps.') ||
      key.startsWith('checks.hubtel_') ||
      key === 'sms.hubtel_sender') &&
    !hasHubtel()
  ) {
    return false;
  }
  if (
    (key.startsWith('payouts.gip_code_') ||
      key === 'payouts.bank_channel' ||
      key.startsWith('pool.')) &&
    !hasAccessBank()
  ) {
    return false;
  }
  if (
    (key.startsWith('topups.route_') || key.startsWith('payouts.route_')) &&
    !hasGhanaMomoNetworks()
  ) {
    return false;
  }
  return true;
}
